import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { api_auth, api_host } from '../../../../../common/globals/constants';
import { AtCircularProgress } from '../../../../../components/AtCircularProgress';
import { withExcelTitleRow } from '../../../../../utils/fns/build-excel-rows';
import { Alert } from '@mui/material';

function buildExcelRows(processedData, duracionTotalAcumulado, t) {
  const headers = [
    t('availability-downtime.table.header.start-time'),
    t('availability-downtime.table.header.end-time'),
    t('availability-downtime.table.header.duration'),
    t('availability-downtime.table.header.type'),
    t('availability-downtime.table.header.error-code'),
  ];
  const headerRow = headers.map((value) => ({ value, fontWeight: 'bold' }));

  const dataRows = processedData.map((row) => {
    const isDowntime = row.tipo === 'downtime';
    const errorCellValue = isDowntime
      ? row.errorCodeLabel
      : t('objective.status.no-monitoring');
    const errorCellColor = isDowntime ? '#FF0000' : '#828282';

    return [
      { value: row.fechaDesde, type: String },
      { value: row.fechaHasta, type: String },
      { value: row.duracionEvento, type: String },
      { value: row.tipo, type: String },
      {
        value: errorCellValue,
        type: String,
        backgroundColor: errorCellColor,
        color: '#FFFFFF',
      },
    ];
  });

  const totalRow = (label, value) => ([
    { value: '', type: String },
    { value: label, type: String },
    { value, type: String },
    { value: '', type: String },
    { value: '', type: String },
  ]);

  return [
    headerRow,
    ...dataRows,
    totalRow(t('availability-downtime.table.footer.total-downtime'), duracionTotalAcumulado.duracionDowntimeAcumulado),
    totalRow(t('availability-downtime.table.footer.total-no-monitoring'), duracionTotalAcumulado.duracionNoMonitoreoAcumulado),
  ];
}

export function AvailabilityDowntimeGlobalWithError({
  objetivoId,
  period,
  statePeriod,
  refreshTime,
  onExportDataReady,
  objectiveName,
}) {
  const { t } = useTranslation();
  const { data: dataAvailability, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'availabilityDowntimeGlobalWithError',
      objetivoId,
      period,
      statePeriod?.startDate ?? '',
      statePeriod?.endDate ?? '',
    ],
    queryFn: async () => {
      try {
        let fechaInicio = '';
        let fechaTermino = '';

        if (period === 'c' && statePeriod) {
          fechaInicio = format(new Date(statePeriod.startDate), 'yyyy-MM-dd');
          fechaTermino = format(new Date(statePeriod.endDate), 'yyyy-MM-dd');
        }
        let token = sessionStorage.getItem('key');
        let user = sessionStorage.getItem('user');
        const params = new URLSearchParams();
        params.append('user', user);
        params.append('token', token);
        params.append('objetivoId', objetivoId);
        params.append('horarioId', '0');
        params.append('t', period);
        params.append('fechaInicio', fechaInicio);
        params.append('fechaTermino', fechaTermino);
        params.append('key', api_auth);
        params.append('nodoId', '0');
        const response = await fetch(
          `${api_host}/reporte/online/indicadores/getEventosDisponibilidadDowntimeGlobal/?${params.toString()}`,
        );
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        return data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: refreshTime,
    retry: false,
  });

  const processedData = React.useMemo(() => {
    if (!dataAvailability) return [];
    return dataAvailability.map((evento) => {
      const duracionEventoMs = moment(evento.fecha_hasta).diff(
        moment(evento.fecha_desde),
      );
      const durationObj = moment.duration(duracionEventoMs);
      const days = Math.floor(durationObj.asDays());
      const duracionEvento = `${days}d ${durationObj.hours()}h ${durationObj.minutes()}m ${durationObj.seconds()}s`;

      /** Si el evento es de tipo downtime se determina cuál fue el primer evento period con codigo_tipo_id distinto de 1 0 0 */
      let codigoErrorPrimerEvento = null;
      if (evento.nombre === 'downtime') {
        for (const eventoPeriodo of evento.eventos_periodo) {
          let primerCodigoError = eventoPeriodo.codigos.find((item) =>
            [2, 3, 4].includes(item.codigo_tipo_id),
          );
          if (primerCodigoError) {
            codigoErrorPrimerEvento = primerCodigoError;
            break;
          }
        }
      }
      const codigoId = codigoErrorPrimerEvento?.codigo_id;
      const detalleCodigo = t(`codes.${codigoId}`, { returnObjects: true });

      const errores = {};
      const errorCounts = {};
      for (const eventoPeriodo of evento.eventos_periodo) {
        for (const codigo of eventoPeriodo.codigos) {
          if ([2, 3, 4].includes(codigo.codigo_tipo_id)) {
            errores[codigo['nombre']] = (errores?.[codigo['nombre']] ?? 0) + 1;
            errorCounts[codigo.codigo_id] = (errorCounts[codigo.codigo_id] ?? 0) + 1;
          }
        }
      }

      const erroresCantidad = Object.entries(
        errores,
      ).map(([error, cantidad]) => ({ error, cantidad }));

      /** Etiqueta con todos los códigos de error, de mayor a menor frecuencia (usada solo en la exportación a Excel) */
      const errorCodeLabel = Object.entries(errorCounts)
        .sort(([, cantidadA], [, cantidadB]) => cantidadB - cantidadA)
        .map(([id, cantidad]) => `${t(`codes.${id}`, { returnObjects: true }).name} (${cantidad})`)
        .join(', ');

      return {
        fechaDesde: format(new Date(evento.fecha_desde), 'dd-MM-yyyy HH:mm'),
        fechaHasta: format(new Date(evento.fecha_hasta), 'dd-MM-yyyy HH:mm'),
        tipo: evento.nombre,
        duracionEvento,
        codigoId,
        detalleCodigo,
        errorCodeLabel,
        duracionEventoMs,
        errores: erroresCantidad,
      };
    });
  }, [dataAvailability]);

  function calcularDuracionTotal() {
    let duracionTotalDowntimeMs = 0;
    let duracionTotalNoMonitoreoMs = 0;

    for (const evento of processedData) {
      if (evento.tipo === 'downtime') {
        duracionTotalDowntimeMs += evento.duracionEventoMs;
      }
      if (evento.tipo === 'no monitoreo') {
        duracionTotalNoMonitoreoMs += evento.duracionEventoMs;
      }
    }

    const durationDowntime = moment.duration(duracionTotalDowntimeMs);
    const durationNoMonitoreo = moment.duration(duracionTotalNoMonitoreoMs);

    const duracionDowntimeAcumulado = `${Math.floor(durationDowntime.asDays())}d ${durationDowntime.hours()}h ${durationDowntime.minutes()}m ${durationDowntime.seconds()}s`;
    const duracionNoMonitoreoAcumulado = `${Math.floor(durationNoMonitoreo.asDays())}d ${durationNoMonitoreo.hours()}h ${durationNoMonitoreo.minutes()}m ${durationNoMonitoreo.seconds()}s`;

    return {
      duracionDowntimeAcumulado,
      duracionNoMonitoreoAcumulado,
    };
  }

  const duracionTotalAcumulado = React.useMemo(
    () => calcularDuracionTotal(),
    [processedData],
  );

  React.useEffect(() => {
    if (!onExportDataReady || isLoading || isFetching || error) return;
    onExportDataReady({
      excelRows: withExcelTitleRow(
        objectiveName,
        buildExcelRows(processedData, duracionTotalAcumulado, t)
      ),
      excelFilename: `${t('availability-downtime.export-filename')}.xlsx`,
    });
  }, [processedData, duracionTotalAcumulado, isLoading, isFetching, error, objectiveName]);

  if (isLoading || isFetching) {
    return (
      <Box>
        <AtCircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {t('availability-downtime.error-fetch-msg')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t('availability-downtime.table.header.start-time')}
              </TableCell>
              <TableCell>
                {t('availability-downtime.table.header.end-time')}
              </TableCell>
              <TableCell>
                {t('availability-downtime.table.header.duration')}
              </TableCell>
              <TableCell>
                {t('availability-downtime.table.header.type')}
              </TableCell>
              <TableCell>
                {t('availability-downtime.table.header.error-code')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t('availability-downtime.table.footer.no-global')}
                </TableCell>
              </TableRow>
            )}
            {processedData.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.fechaDesde}</TableCell>
                <TableCell>{row.fechaHasta}</TableCell>
                <TableCell>{row.duracionEvento}</TableCell>
                <TableCell>{row.tipo}</TableCell>
                <TableCell>
                  {row.tipo === 'downtime' ? (
                    <Tooltip title={row.detalleCodigo.description}>
                      <Chip
                        size="small"
                        label={row.detalleCodigo.name}
                        style={{ backgroundColor: 'red', color: 'white' }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      label={t('objective.status.no-monitoring')}
                      style={{ backgroundColor: '#828282', color: 'white' }}
                    />
                  )}
                  {row.tipo === 'downtime' && (
                    <BtnErroresComunes id={index} errores={row.errores} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2}>
                {t('availability-downtime.table.footer.total-downtime')}
              </TableCell>
              <TableCell colSpan={3}>
                {duracionTotalAcumulado.duracionDowntimeAcumulado}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={2}>
                {t('availability-downtime.table.footer.total-no-monitoring')}
              </TableCell>
              <TableCell colSpan={3}>
                {duracionTotalAcumulado.duracionNoMonitoreoAcumulado}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BtnErroresComunes({ id, errores }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  return (
    <React.Fragment>
      <Tooltip title={t('availability-downtime.table.header.error-code')}>
        <IconButton
          id={id}
          onClick={handleClick}
          size="small"
          style={{ marginLeft: 5 }}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box padding={1}>
          <Typography variant="subtitle2">
            {t('availability-downtime.errors')}
          </Typography>
          <List>
            {errores.map(({ error, cantidad }) => (
              <ListItem
                key={error}
                style={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              >
                <ListItemText>
                  {error}: {cantidad}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </React.Fragment>
  );
}
